import { apiCall } from '../../services/apiUtil';

/**
 * Team API
 *
 * Handles team operations:
 * - Create and manage teams
 * - Team members management
 * - Team queries and filters
 */
export const teamAPI = {
  /**
   * Create a new team
   * @param {Object} teamData - Team data
   * @returns {Promise} Created team data
   */
  create: async (teamData) => {
    const payload = {
      teamName: teamData.name,  // Backend expects 'teamName', not 'name'
      description: teamData.description,
      teamLogo: teamData.logo,  // Backend expects 'teamLogo', not 'logo'
      maxMembers: teamData.maxPlayers || 20,  // Map maxPlayers to maxMembers
      captainUserId: teamData.createdBy,  // Map createdBy to captainUserId
      ownerType: teamData.ownerType || 'user',  // 'user' or 'group'
      ownerId: teamData.ownerId,  // ID of the owner (user or group)
      sportTypeId: teamData.sportTypeId,  // Sport type ID
    };

    return apiCall('/teams', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get team by ID
   * @param {number} id - Team ID
   * @returns {Promise} Team data
   */
  getById: async (id) => {
    return apiCall(`/teams/${id}`);
  },

  /**
   * Get all teams with optional filters
   * @param {Object} filters - Filter options
   * @returns {Promise} Array of teams
   */
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.page)        queryParams.append('page', filters.page);
    if (filters.limit)       queryParams.append('limit', filters.limit);
    if (filters.search)      queryParams.append('search', filters.search);
    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    if (filters.createdBy)   queryParams.append('createdBy', filters.createdBy);
    if (filters.ownerType)   queryParams.append('ownerType', filters.ownerType);
    if (filters.ownerId)     queryParams.append('ownerId', filters.ownerId);

    const queryString = queryParams.toString();
    return apiCall(queryString ? `/teams?${queryString}` : '/teams');
  },

  /**
   * Get teams created by a specific user
   * @param {number} createdBy - User ID
   * @returns {Promise} Paginated teams
   */
  getMyTeams: async (createdBy, filters = {}) => {
    const queryParams = new URLSearchParams({ createdBy });
    if (filters.page)        queryParams.append('page', filters.page);
    if (filters.limit)       queryParams.append('limit', filters.limit);
    if (filters.search)      queryParams.append('search', filters.search);
    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    return apiCall(`/teams?${queryParams.toString()}`);
  },

  /**
   * Get teams by owner (user or group)
   * @param {string} ownerType - 'user' or 'group'
   * @param {string} ownerId - Owner ID
   * @returns {Promise} Paginated teams
   */
  getByOwner: async (ownerType, ownerId, filters = {}) => {
    const queryParams = new URLSearchParams({ ownerType, ownerId });
    if (filters.page)        queryParams.append('page', filters.page);
    if (filters.limit)       queryParams.append('limit', filters.limit);
    if (filters.search)      queryParams.append('search', filters.search);
    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    return apiCall(`/teams?${queryParams.toString()}`);
  },

  /**
   * Get teams the caller can register for a tournament: their permanent
   * (user/group) teams unioned with teams created for this tournament,
   * with already-enrolled teams surfaced first regardless of page.
   * @param {number} tournamentId - Tournament ID
   * @param {string} ownerType - 'user' or 'group' (owner of the permanent teams)
   * @param {string} ownerId - Owner ID of the permanent teams
   * @param {Object} filters - { page, limit, sportTypeId }
   * @returns {Promise} Paginated teams with isEnrolled/ownerCategory flags
   */
  getRegisterableTeams: async (tournamentId, ownerType, ownerId, filters = {}) => {
    const queryParams = new URLSearchParams({ ownerType, ownerId });
    if (filters.page)        queryParams.append('page', filters.page);
    if (filters.limit)       queryParams.append('limit', filters.limit);
    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    return apiCall(`/tournaments/${tournamentId}/registerable-teams?${queryParams.toString()}`);
  },

  /**
   * Update team
   * @param {number} id - Team ID
   * @param {Object} teamData - Updated team data
   * @returns {Promise} Updated team data
   */
  update: async (id, teamData) => {
    return apiCall(`/teams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(teamData),
    });
  },

  /**
   * Delete team
   * @param {number} id - Team ID
   * @returns {Promise} API response
   */
  delete: async (id) => {
    return apiCall(`/teams/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add a player to team
   * @param {number} teamId - Team ID
   * @param {Object} playerData - Player data
   * @returns {Promise} API response
   */
  addPlayer: async (teamId, playerData) => {
    return apiCall(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({
        userId: playerData.userId,
        role: playerData.role || 'member',
      }),
    });
  },

  /**
   * Remove a player from team
   * @param {number} teamId - Team ID
   * @param {number} playerId - Player ID
   * @returns {Promise} API response
   */
  removePlayer: async (teamId, playerId) => {
    return apiCall(`/teams/${teamId}/members/${playerId}`, {
      method: 'DELETE',
    });
  },
};

export default teamAPI;
